import { useState } from "react";
import { useNavigate } from "react-router-dom";
import imgWhiteLogo1 from "../assets/2ccda97ecaa4b401fe79d7f0575e654788a45c27.png";

interface DonationOption {
  amount: number;
  label: string;
  oneTimeUrl: string; // One-time payment Stripe URL
  monthlyUrl: string; // Monthly subscription Stripe URL
  isCustom?: boolean;
}

const CUSTOM_DONATION_LINK = "https://buy.stripe.com/bJeeVd9qhaZ06tH7j1eAg09";

const donationOptions: DonationOption[] = [
  {
    amount: 5,
    label: "$5",
    oneTimeUrl: "https://buy.stripe.com/28E7sL8md6IKbO10UDeAg01",
    monthlyUrl: "https://buy.stripe.com/4gM4gzdGx3wy5pD9r9eAg05",
  },
  {
    amount: 10,
    label: "$10",
    oneTimeUrl: "https://buy.stripe.com/fZu5kDgSJ6IKg4h1YHeAg02",
    monthlyUrl: "https://buy.stripe.com/aFafZhaul5EG6tH0UDeAg06",
  },
  {
    amount: 25,
    label: "$25",
    oneTimeUrl: "https://buy.stripe.com/aFa9AT8md5EG2dr8n5eAg03",
    monthlyUrl: "https://buy.stripe.com/cNi8wP1XP7MO4lzdHpeAg07",
  },
  {
    amount: 50,
    label: "$50",
    oneTimeUrl: "https://buy.stripe.com/6oUdR9dGx8QS9FTfPxeAg04",
    monthlyUrl: "https://buy.stripe.com/7sY14n45Xebc8BPdHpeAg08",
  },
  {
    amount: 0,
    label: "Choose Amount",
    oneTimeUrl: CUSTOM_DONATION_LINK,
    monthlyUrl: CUSTOM_DONATION_LINK,
    isCustom: true,
  },
];

function DonationCard({
  option,
  onSelect,
  isSelected,
}: {
  option: DonationOption;
  onSelect: () => void;
  isSelected: boolean;
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex flex-col p-4 rounded-lg border-2 transition-all duration-200 ${
        isSelected
          ? "border-white bg-white/10 scale-105"
          : "border-white/30 hover:border-white/60 hover:bg-white/5"
      }`}
    >
      <div className="text-3xl font-bold text-white mb-1">
        {option.isCustom ? "Choose amount" : option.label}
      </div>
      {option.isCustom && <p className="text-sm text-white/70">Pick your own gift amount.</p>}
    </button>
  );
}

export default function DonatePage() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<DonationOption | null>(null);
  const [isMonthly, setIsMonthly] = useState(false);

  const selectOption = (option: DonationOption) => {
    setSelectedOption(option);
  };

  const getCtaLabel = () => {
    if (!selectedOption) return "Select an amount to continue";
    return `${isMonthly ? "Subscribe" : "Donate"} ${selectedOption.label}${isMonthly ? "/month" : ""}`;
  };

  const isCtaDisabled = !selectedOption;

  const handleDonate = () => {
    if (!selectedOption) return;
    const targetUrl = isMonthly ? selectedOption.monthlyUrl : selectedOption.oneTimeUrl;
    if (!targetUrl) return;

    // Redirect to Stripe Checkout with the selected amount's URL based on payment type
    window.location.href = targetUrl;
  };

  return (
    <div className="min-h-screen w-full bg-[#00499d] flex flex-col">
      {/* Simple Header */}
      <header className="bg-[#00499d] w-full border-b border-white/20 py-4">
        <div className="max-w-[1200px] mx-auto px-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white flex items-center gap-2 hover:opacity-80">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div className="flex items-center gap-3">
            <img src={imgWhiteLogo1} alt="Project Ontario" className="h-8 w-8" />
            <div className="text-white font-black tracking-tight leading-5">
              <div>PROJECT</div>
              <div>ONTARIO</div>
            </div>
          </div>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-[800px] w-full">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Support Project Ontario
            </h1>
            <p className="text-lg text-white/90 max-w-[600px] mx-auto mb-4">
              Project Ontario is a non-profit organization funded by Ontarians who believe our province can do better.
              Your financial support helps us publish new ideas, host public events, and build momentum for serious
              conservative reform.
            </p>
            <p className="text-sm text-white/70">
              Project Ontario is not a registered charity, and donations are not eligible for a tax receipt.
            </p>
          </div>

          {/* Payment Type Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/10 rounded-full p-1 flex gap-1">
              <button
                onClick={() => setIsMonthly(false)}
                className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
                  !isMonthly
                    ? "bg-white text-[#00499d]"
                    : "text-white hover:bg-white/10"
                }`}
              >
                One-Time
              </button>
              <button
                onClick={() => setIsMonthly(true)}
                className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
                  isMonthly
                    ? "bg-white text-[#00499d]"
                    : "text-white hover:bg-white/10"
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          {/* Donation Options */}
          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-6">
              Choose Your {isMonthly ? "Monthly" : "One-Time"} Contribution
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {donationOptions.map((option) => (
                <DonationCard
                  key={option.amount}
                  option={option}
                  onSelect={() => selectOption(option)}
                  isSelected={selectedOption?.amount === option.amount}
                />
              ))}
            </div>
          </div>

          {selectedOption?.isCustom && (
            <p className="text-center text-white/80 text-sm md:text-base mb-8">
              You&apos;ll choose your specific amount on the secure Stripe checkout page.
            </p>
          )}

          {/* Proceed Button */}
          <div className="flex justify-center">
            <button
              onClick={handleDonate}
              disabled={isCtaDisabled}
              className={`px-6 py-3 rounded-full font-bold text-base transition-all duration-200 ${
                !isCtaDisabled
                  ? "bg-white text-[#00499d] hover:scale-105 cursor-pointer"
                  : "bg-white/30 text-white/50 cursor-not-allowed"
              }`}
            >
              {getCtaLabel()}
            </button>
          </div>

          {/* Security Badge */}
          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm flex items-center justify-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secured by Stripe - Your payment information is encrypted and secure
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
